import type { Contact } from '../../domain/contact';
import type { CollectedDataItem } from '../../domain/message';
import { CountryService } from '../country/country';
import UrlService from '../UrlService';

import SharedDataExtractionService from './sharedDataExtractionService';

const createSalesNavDataExtractionService = () => {
  const { extractName, extractTitle, extractCountry, extractContentByKey } = SharedDataExtractionService;

  const extractCompanyNames = (collectedData: CollectedDataItem[]): string[] => {
    const companyElem = collectedData.find(({ name }) => name === 'Company Names');
    return (Array.isArray(companyElem?.content) && companyElem.content) || [];
  };

  const extractCompanyLinkedinIds = (collectedData: CollectedDataItem[]): number[] | undefined => {
    const companyElem = collectedData.find(({ name }) => name === 'Company Names');
    if (!companyElem || !companyElem.attributes?.['href'] || !Array.isArray(companyElem.attributes['href'])) {
      return;
    }

    const pathnameArr = companyElem.attributes['href'];

    return pathnameArr
      .map((pathname) => {
        const id = UrlService.getCompanyLinkedinIdFromSalesNavPathname(pathname);
        return parseInt(id ?? '');
      })
      .filter(Boolean);
  };

  const extractContactInfo = (collectedData: CollectedDataItem[]): Partial<Contact> => {
    const name = extractName(collectedData);
    const title = extractTitle(collectedData);
    const country = extractCountry(collectedData);
    const numericCompanyLinkedinIds = extractCompanyLinkedinIds(collectedData) || [];
    const companyNames = extractCompanyNames(collectedData);
    const companyNamesNoLink: string[] =
      (extractContentByKey(collectedData, 'Company Names No Link') as string[]) || [];

    const allCompanyNames = [...companyNames, ...companyNamesNoLink].filter(Boolean);

    return {
      name,
      title,
      country,
      numericCompanyLinkedinIds,
      companyNames: allCompanyNames,
      companyName: allCompanyNames[0],
    };
  };

  const extractSearchLocations = (collectedData: CollectedDataItem[]): string[] => {
    const locations = (collectedData.find((item) => item.name === 'Search locations')?.content ?? []) as string[];
    return locations.map((location) => {
      const possibleCountry = location.split(', ').at(-1)!.trim();
      return CountryService.suggestCountry(possibleCountry)?.text ?? location;
    });
  };

  const extractResultsCount = (collectedData: CollectedDataItem[]): number => {
    const parseResults = (input: string) => {
      const match = input.match(/^(\d+(\.\d+)?)([KkMm]?)\+?/);
      if (!match) {
        return 0;
      }

      const numberPart = parseFloat(match[1]);
      const magnitudePart = match[3];

      switch (magnitudePart.toLowerCase()) {
        case 'k':
          return numberPart * 1_000;
        case 'm':
          return numberPart * 1_000_000;
        default:
          return numberPart;
      }
    };

    const resultsCountString = collectedData.find((item) => item.name === 'Results count')?.content as string;
    if (!resultsCountString) {
      return 0;
    }

    return parseResults(resultsCountString);
  };

  const extractSalesNavLinkedinId = (inputString: string): string => {
    const pattern = /\/sales\/lead\/([A-Za-z0-9_-]+),/;

    const match = inputString.match(pattern);

    return (match && match[1]) || '';
  };

  const extractContactCompanyNameFromString = (company: string | undefined): string =>
    company ? company.replace(/\(\+\d+\)/g, '').trim() : '';

  const extractContactSearchInfo = (collectedData: CollectedDataItem[]): Partial<Contact>[] => {
    const names = (collectedData.find((item) => item.name === 'Search names')?.content ?? []) as string[];
    const links = (collectedData.find((item) => item.name === 'Search names')?.attributes?.href ?? []) as string[];
    const positions = collectedData.find((item) => item.name === 'Search positions')?.content ?? [];
    const locations = extractSearchLocations(collectedData);
    const companies = collectedData.find((item) => item.name === 'Search companies');
    const companyNames = Array.isArray(companies?.content) ? companies?.content : [];
    const companyPaths = (companies?.attributes?.href as string[]) || [];
    const companyLinkedinIds = companyPaths.map(
      (path) => path && UrlService.getCompanyLinkedinIdFromSalesNavPathname(path)
    );
    const summaries = collectedData.find((item) => item.name === 'Search summaries')?.content ?? [];

    return names.map((name, index) => {
      const companyNameFromCompanies = extractContactCompanyNameFromString(companyNames[index]);
      const companyNameFromSummary = extractContactCompanyNameFromSummary({
        title: positions[index],
        summary: summaries[index],
      });
      return {
        linkedinSalesNavId: extractSalesNavLinkedinId(links[index]),
        name,
        title: positions[index],
        country: locations[index],
        companyLinkedinId: companyLinkedinIds[index],
        numericCompanyLinkedinIds: companyLinkedinIds[index] ? [parseInt(companyLinkedinIds[index])] : [],
        companyName: companyNameFromCompanies || companyNameFromSummary,
        matchedProfiles: [],
        hasDirectPhoneNumbers: false,
        hasMobilePhoneNumbers: false,
        hasValidEmails: false,
      };
    });
  };

  const extractContactCompanyFromTitle = (jobTitle?: string): string => {
    if (!jobTitle) {
      return '';
    }

    const pattern = /(?:\sat\s|\s@\s)([^]+)/i;
    const match = pattern.exec(jobTitle);
    const result = (match && match[1])?.trim() || '';

    if (result) {
      return result[result.length - 1] === '.' ? result.slice(0, -1) : result;
    } else {
      return '';
    }
  };

  const extractContactCompanyFromSummary = ({
    jobTitle,
    summaryText,
  }: {
    jobTitle?: string;
    summaryText: string;
  }): string => {
    if (jobTitle && jobTitle.includes(summaryText) && jobTitle.includes(' at ')) {
      const parts = jobTitle.split(' at ');
      const organization = parts[1].trim();

      if (organization.length > 0) {
        return organization;
      }
    }

    return '';
  };

  const extractContactCompanyFromCurrent = (jobTitle?: string): string =>
    extractContactCompanyFromSummary({ jobTitle, summaryText: 'Current:' });

  const extractContactCompanyFromPast = (jobTitle?: string): string =>
    extractContactCompanyFromSummary({ jobTitle, summaryText: 'Past:' });

  const extractContactCompanyName = ({ title, summary }: { title?: string; summary?: string }): string =>
    extractContactCompanyFromTitle(title) ||
    extractContactCompanyFromCurrent(summary) ||
    extractContactCompanyFromPast(summary);

  const extractContactCompanyNameFromSummary = ({
    title = '',
    summary = '',
  }: {
    title?: string;
    summary?: string;
  }): string => summary.replace(title, '').trim() || '';

  return {
    extractContactInfo,
    extractContactSearchInfo,
    extractContactCompanyName,
    extractContactCompanyNameFromString,
    extractResultsCount,
    extractSalesNavLinkedinId,
  };
};

const SalesNavDataExtractionService = createSalesNavDataExtractionService();

export default SalesNavDataExtractionService;
